import { Schema } from "effect";

// =============================================================================
// CPU Units - Branded Types
// =============================================================================

/**
 * CPU in millicores (1000 millicores = 1 core).
 * Used by: Prometheus metrics, K8s API allocatable, internal calculations.
 * Examples: 100 (100m), 500 (500m), 1000 (1 core), 2500 (2.5 cores)
 */
export const Millicores = Schema.Finite.pipe(
  Schema.brand("Millicores")
);
export type Millicores = typeof Millicores.Type;

/**
 * CPU in cores (1 core = 1000 millicores).
 * Used by: PodMetrics from K8s metrics-server (via parseCpuValue).
 * Examples: 0.1 (100m), 0.5 (500m), 1.0 (1 core), 2.5 (2.5 cores)
 */
export const Cores = Schema.Finite.pipe(
  Schema.brand("Cores")
);
export type Cores = typeof Cores.Type;

// =============================================================================
// Memory Units - Branded Types
// =============================================================================

/**
 * Memory in bytes.
 * Used by: All memory values in K8s (usage, limits, requests).
 * Examples: 268435456 (256Mi), 1073741824 (1Gi)
 */
export const Bytes = Schema.Int.pipe(Schema.brand("Bytes"));
export type Bytes = typeof Bytes.Type;

// =============================================================================
// Time Units - Branded Types
// =============================================================================

/**
 * Time duration in milliseconds.
 * Used by: Performance timings, analysis durations, timeouts.
 * Examples: 100 (100ms), 1000 (1 second), 60000 (1 minute)
 */
export const Milliseconds = Schema.Finite.pipe(
  Schema.brand("Milliseconds")
);
export type Milliseconds = typeof Milliseconds.Type;

// =============================================================================
// Conversion Functions
// =============================================================================

/** Convert cores to millicores: 1.0 -> 1000 */
export const coresToMillicores = (
  cores: Cores
): Millicores => Millicores.make(cores * 1000);

/** Convert millicores to cores: 1000 -> 1.0 */
export const millicoresToCores = (
  millicores: Millicores
): Cores => Cores.make(millicores / 1000);

// =============================================================================
// Factory Functions (for creating branded values from raw numbers)
// =============================================================================

/** Create Millicores from raw number */
export const millicores = (value: number): Millicores =>
  Millicores.make(value);

/** Create Cores from raw number */
export const cores = (value: number): Cores =>
  Cores.make(value);

/** Create Bytes from raw number */
export const bytes = (value: number): Bytes =>
  Bytes.make(Math.round(value));

/** Create Milliseconds from raw number */
export const milliseconds = (value: number): Milliseconds =>
  Milliseconds.make(value);

// =============================================================================
// Percentage - Branded Type
// =============================================================================

/**
 * Percentage value (0-100).
 * Used by: Threshold configurations, utilization metrics, disk usage.
 * Examples: 80 (80%), 95 (95%)
 */
export const Percentage = Schema.Finite.pipe(
  Schema.check(
    Schema.isGreaterThanOrEqualTo(0),
    Schema.isLessThanOrEqualTo(100)
  ),
  Schema.brand("Percentage")
);
export type Percentage = typeof Percentage.Type;

// =============================================================================
// Multiplier - Branded Type
// =============================================================================

/**
 * Multiplier value (typically 1.0+).
 * Used by: Over-provisioning detection hysteresis.
 * Examples: 1.0 (no hysteresis), 2.0 (50% savings required), 3.0 (66% savings)
 */
export const Multiplier = Schema.Finite.pipe(
  Schema.check(Schema.isGreaterThanOrEqualTo(1)),
  Schema.brand("Multiplier")
);
export type Multiplier = typeof Multiplier.Type;

// =============================================================================
// RestartCount - Branded Type
// =============================================================================

/**
 * Container restart count.
 * Used by: Pod metrics, issue thresholds.
 * Examples: 0 (no restarts), 5 (5 restarts), 10 (10 restarts)
 */
export const RestartCount = Schema.Int.pipe(
  Schema.check(Schema.isGreaterThanOrEqualTo(0)),
  Schema.brand("RestartCount")
);
export type RestartCount = typeof RestartCount.Type;

// =============================================================================
// Factory Functions (for creating branded values from raw numbers)
// =============================================================================

/** Create Percentage from raw number */
export const percentage = (value: number): Percentage =>
  Percentage.make(value);

/** Create Multiplier from raw number */
export const multiplier = (value: number): Multiplier =>
  Multiplier.make(value);

/** Create RestartCount from raw number */
export const restartCount = (value: number): RestartCount =>
  RestartCount.make(value);

// =============================================================================
// Formatting Functions
// =============================================================================
// NOTE: Formatters moved to @vivus/common/k8s/formatters.ts
// Use formatMillicores, formatCores, formatBytes from there
